import { Injectable, NgZone, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';

import { AuthService } from './auth.service';
import { OrgContextService } from './org-context.service';

export interface ProductAccessConfig {
  enabledProducts: string[];
  roleProductAccess: Record<string, string[]>;
}

const BUILD_PRODUCT_ID = 'build';

const SAFE_WHEN_DENIED_PREFIXES = [
  '/product-disabled',
  '/login',
  '/__/auth',
  '/cli-device',
  '/account',
  '/share',
];

function isSafeWhenDenied(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return SAFE_WHEN_DENIED_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix + '/'),
  );
}

/**
 * Pure access evaluator — mirrors dev-console/admin-console semantics.
 *
 *   1. No config → DENIED.
 *   2. Org level: enabledProducts must include the product.
 *   3. Role level: a non-empty roleProductAccess[roleId] is an allowlist.
 *      Missing entry or empty array = no restriction for that role.
 */
export function evaluateAccess(
  config: ProductAccessConfig | null,
  roleId: string | null,
  productId: string,
): boolean {
  if (!config) return false;
  if (!config.enabledProducts.includes(productId)) return false;

  if (roleId) {
    const roleProducts = config.roleProductAccess[roleId];
    if (Array.isArray(roleProducts) && roleProducts.length > 0 && !roleProducts.includes(productId)) {
      return false;
    }
  }

  return true;
}

@Injectable({ providedIn: 'root' })
export class ProductAccessService {
  private readonly auth = inject(AuthService);
  private readonly orgContext = inject(OrgContextService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private readonly config = signal<ProductAccessConfig | null>(null);
  private readonly loaded = signal(false);
  private readonly currentRoleId = signal<string | null>(null);
  private readonly memberExists = signal(true);

  private loadedOrgId: string | null = null;
  private configUnsubscribe: Unsubscribe | null = null;
  private memberUnsubscribe: Unsubscribe | null = null;
  private configReceived = false;
  private memberReceived = false;
  private loadedResolve: (() => void) | null = null;
  private loadedPromise: Promise<void> = new Promise((resolve) => {
    this.loadedResolve = resolve;
  });

  readonly enabledProducts = computed(() => this.config()?.enabledProducts ?? []);
  readonly isLoaded = computed(() => this.loaded());

  constructor() {
    effect(() => {
      const user = this.auth.currentUser();
      const orgId = this.orgContext.currentOrg()?.id ?? null;

      if (this.auth.isLoading()) return;

      if (!user) {
        this.clearCache();
        return;
      }

      if (!orgId) return;

      if (orgId !== this.loadedOrgId) {
        this.startListening(orgId, user.uid);
      }
    });

    effect(() => {
      const cfg = this.config();
      const roleId = this.currentRoleId();
      const isLoaded = this.loaded();
      const hasMember = this.memberExists();

      if (!isLoaded) return;

      const hasAccess = hasMember && evaluateAccess(cfg, roleId, BUILD_PRODUCT_ID);
      const currentUrl = this.router.url;

      if (!hasAccess && !isSafeWhenDenied(currentUrl)) {
        this.router.navigate(['/product-disabled']);
        return;
      }

      if (hasAccess && currentUrl.split('?')[0].split('#')[0] === '/product-disabled') {
        this.router.navigate(['/dashboard']);
      }
    });
  }

  /**
   * Returns a computed signal indicating whether the current user can access
   * the given product.
   */
  hasProductAccess(productId: string) {
    return computed(() => {
      if (!this.loaded()) return false;
      if (!this.memberExists()) return false;
      return evaluateAccess(this.config(), this.currentRoleId(), productId);
    });
  }

  /**
   * Async check for use in route guards.
   */
  async checkProductAccess(productId: string): Promise<boolean> {
    await this.waitForAuth();

    const user = this.auth.currentUser();
    if (!user) return false;

    try {
      const orgId = await this.resolveOrganizationId(user.uid);

      if (!orgId) {
        // Preserve the previous Build behavior: no org context should not be
        // treated as product denial here; org-specific pages will fail later if
        // an organization is actually required.
        return true;
      }

      if (orgId !== this.loadedOrgId) {
        this.startListening(orgId, user.uid);
      }

      if (!this.loaded()) {
        await this.loadedPromise;
      }

      return this.memberExists() && evaluateAccess(this.config(), this.currentRoleId(), productId);
    } catch (err) {
      console.error('Failed to check product access:', err);
      if (this.isPhantomAuth(err)) {
        this.recoverFromPhantomAuth();
        // Block while the reload runs.
        return false;
      }
      // Allow on error to avoid blocking users due to transient failures
      return true;
    }
  }

  clearCache(): void {
    this.stopListening();
    this.config.set(null);
    this.currentRoleId.set(null);
    this.memberExists.set(true);
    this.loaded.set(false);
    this.loadedOrgId = null;
    this.configReceived = false;
    this.memberReceived = false;
    this.resetLoadedPromise();
  }

  private async waitForAuth(): Promise<void> {
    if (!this.auth.isLoading()) return;

    await new Promise<void>((resolve) => {
      const check = setInterval(() => {
        if (!this.auth.isLoading()) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    });
  }

  private async resolveOrganizationId(uid: string): Promise<string | null> {
    const currentOrgId = this.orgContext.currentOrg()?.id;
    if (currentOrgId) return currentOrgId;

    await this.orgContext.init();

    const initializedOrgId = this.orgContext.currentOrg()?.id;
    if (initializedOrgId) return initializedOrgId;

    const db = getFirestore();
    const userDoc = await getDoc(doc(db, 'users', uid));
    const userData = userDoc.data();
    const orgIds: string[] = userData?.['organizationIds'] ?? [];

    return orgIds[0] ?? null;
  }

  private startListening(organizationId: string, uid: string): void {
    this.stopListening();
    this.loadedOrgId = organizationId;
    this.configReceived = false;
    this.memberReceived = false;
    this.loaded.set(false);
    this.resetLoadedPromise();

    const db = getFirestore();
    const configRef = doc(db, 'organizations', organizationId, 'product-access', 'config');
    const memberRef = doc(db, 'organizations', organizationId, 'members', uid);

    this.configUnsubscribe = onSnapshot(configRef, (snapshot) => {
      this.zone.run(() => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          this.config.set({
            enabledProducts: data['enabledProducts'] || [],
            roleProductAccess: data['roleProductAccess'] || {},
          });
        } else {
          this.config.set(null);
        }

        this.configReceived = true;
        this.checkFullyLoaded();
      });
    }, (error) => {
      console.error('Product access config snapshot error:', error);
      this.zone.run(() => {
        this.config.set(null);
        this.configReceived = true;
        this.checkFullyLoaded();
      });
    });

    this.memberUnsubscribe = onSnapshot(memberRef, (snapshot) => {
      this.zone.run(() => {
        this.memberExists.set(snapshot.exists());
        const data = snapshot.data();
        this.currentRoleId.set(data?.['roleId'] || data?.['role'] || null);
        this.memberReceived = true;
        this.checkFullyLoaded();
      });
    }, (error) => {
      console.error('Product access member snapshot error:', error);
      this.zone.run(() => {
        this.memberExists.set(true);
        this.currentRoleId.set(null);
        this.memberReceived = true;
        this.checkFullyLoaded();
      });
    });
  }

  private stopListening(): void {
    this.configUnsubscribe?.();
    this.memberUnsubscribe?.();
    this.configUnsubscribe = null;
    this.memberUnsubscribe = null;
  }

  private checkFullyLoaded(): void {
    if (!this.configReceived || !this.memberReceived) return;

    this.loaded.set(true);
    this.loadedResolve?.();
    this.loadedResolve = null;
  }

  private resetLoadedPromise(): void {
    this.loadedPromise = new Promise((resolve) => {
      this.loadedResolve = resolve;
    });
  }

  /**
   * "Phantom auth" = Firestore returns permission-denied while our cached
   * Firebase Auth user object is still set. This happens when IndexedDB is
   * wiped externally (e.g. user clears site data via DevTools) — the in-memory
   * user lingers but the underlying credentials are dead, so every read fails.
   * Recovery: reload the page so Firebase Auth re-reads IndexedDB, finds
   * nothing, and signs the user out cleanly.
   */
  private isPhantomAuth(error: any): boolean {
    return error?.code === 'permission-denied' && !!getAuth().currentUser;
  }

  private phantomAuthRecoveryTriggered = false;
  private recoverFromPhantomAuth(): void {
    if (this.phantomAuthRecoveryTriggered) return;
    this.phantomAuthRecoveryTriggered = true;
    console.warn('[ProductAccess] Phantom auth detected (permission-denied while user still set) — reloading to recover');
    window.location.reload();
  }
}
